import fileStore                    from '@/stores/fileStore';
import imageOf                      from '@/components/imageOf';
import ProjectModel                 from '@models/map/ProjectModel';
import StonecodeFileFileModel       from '@models/file/StonecodeFileFileModel';
import updateSingleFileForModelUrn  from '@/helpers/updateSingleFileForModelUrn';
import { TokaMapInstance }          from '@tokamap/core';

/** Le `pixelRatio: 1` de la capture est ce qui rend cette largeur réelle : sans lui, la densité de
 * l'écran qui capture s'y multiplierait. */
const THUMBNAIL_MAX_WIDTH = 640;
const THUMBNAIL_TYPE = 'image/jpeg';
const THUMBNAIL_QUALITY = .75;

/**
 * Marque de nos propres aperçus. L'API n'a qu'un seul type d'image de projet : la capture et l'image
 * déposée par un utilisateur partagent le même emplacement, et rien d'autre que ce nom ne les
 * distingue — or on ne remplace jamais une image qu'on n'a pas produite.
 */
const AUTO_THUMBNAIL_STEM = 'apercu-automatique';

export const AUTO_THUMBNAIL_FILE_NAME = `${AUTO_THUMBNAIL_STEM}.jpg`;

export function projectImage(project: ProjectModel) {
	return imageOf(project, 'project_image');
}

/** Sur le nom sans son extension : un aperçu enregistré sous un autre format reste le nôtre. */
export function isAutoThumbnail(file: StonecodeFileFileModel | undefined) {
	return !!file?.name?.startsWith(`${AUTO_THUMBNAIL_STEM}.`);
}

/** Ne décide de rien : `useProjectThumbnail` a déjà vérifié qu'il y avait lieu de capturer. */
export default async function captureProjectThumbnail(project: ProjectModel, instance: TokaMapInstance) {
	const { blob, skippedImageShapeIds } = await instance.captureImage({
		type: THUMBNAIL_TYPE,
		quality: THUMBNAIL_QUALITY,
		maxWidth: THUMBNAIL_MAX_WIDTH,
		pixelRatio: 1,
	});

	// Seule trace d'un aperçu incomplet : la capture saute les images servies sans en-tête CORS
	// plutôt que d'échouer.
	if (skippedImageShapeIds.length) {
		console.warn(`Aperçu du projet : ${skippedImageShapeIds.length} image(s) de forme absente(s) de la capture (en-têtes CORS manquants).`);
	}

	const previous = projectImage(project);

	const created = await updateSingleFileForModelUrn(
		project.urn,
		'project_image',
		new File([blob], AUTO_THUMBNAIL_FILE_NAME, { type: blob.type }),
		previous,
	);

	// On écrit ce qu'on vient de faire plutôt que de le redemander : le service de fichiers met un
	// instant à indexer un fichier neuf, et la liste rapporterait encore l'aperçu précédent.
	fileStore.forget(project, f => f.typeSource.type.reference === 'project_image');

	if (created) fileStore.remember(project, created);
}
